import React, { memo } from "react";
import { Box, Button, Container, Typography, Grid2 } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { RootState } from "../../Store/store";
import { setToggleAuthModal } from "../../Redux/authReducer";
import FeaturesBanner from "./Components/FeaturesBanner";
import FeatureCard from "./Components/FeatureCard";

// Assets
import heroBanner from "./Assets/cute-romantic-black-couple-reading-book-couch.webp";
import iconRecords from "./Assets/Vector Smart Object.webp";
import iconRepairs from "./Assets/Vector Smart Object-1.webp";
import iconAlerts from "./Assets/Vector Smart Object-2.webp";
import iconPhotos from "./Assets/Vector Smart Object-3.webp";
import iconProviders from "./Assets/Vector Smart Object-4.webp";
import iconValue from "./Assets/Vector Smart Object-5.webp";
import iconInsurance from "./Assets/Vector Smart Object-6.webp";
import iconSale from "./Assets/Vector Smart Object-7.webp";
import iconMoving from "./Assets/Vector Smart Object-8.webp";
import iconNeighborhood from "./Assets/Vector Smart Object-9.webp";

const featuresData = [
  {
    icon: iconRecords,
    title: "Organize All Home Records",
    description:
      "Store and access warranties, receipts, permits, and insurance in one secure digital place.",
  },
  {
    icon: iconRepairs,
    title: "Track Repairs & Maintenance",
    description:
      "Log repairs, set reminders, and maintain a clear history of all work done on your property.",
  },
  {
    icon: iconAlerts,
    title: "Smart Maintenance Alerts",
    description:
      "Get customized reminders for routine tasks based on your home's age, type, and location.",
  },
  {
    icon: iconPhotos,
    title: "Store Project Photos & Costs",
    description:
      "Keep visual records and expenses tied to each improvement or contractor visit.",
  },
  {
    icon: iconProviders,
    title: "Find Trusted Service Providers",
    description:
      "Get matched with vetted, insured, and reviewed local pros when you need work done.",
  },
  {
    icon: iconValue,
    title: "Document Home Value Improvements",
    description:
      "Track upgrades that boost resale value and make insurance claims easier.",
  },
  {
    icon: iconInsurance,
    title: "Save on Insurance (Optional)",
    description:
      "Get rebates and recommendations to optimize your home insurance coverage.",
  },
  {
    icon: iconSale,
    title: "Prepare for Sale or Refinance",
    description:
      "Log repairs, set reminders, and maintain a clear history of all work done on your property.",
  },
  {
    icon: iconMoving,
    title: "Simplify Moving",
    description:
      "Seamlessly transfer home data to new owners or store it for your next property.",
  },
  {
    icon: iconNeighborhood,
    title: "Join a Smarter Neighborhood",
    description:
      "Opt in to community dashboards, group discounts, and localized insights.",
    comingSoon: true,
  },
];

const Features = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state?.auth || {}
  );

  const isLoggedIn =
    isAuthenticated &&
    user &&
    Object.keys(user || {}).length > 0;

  const handleSignUpClick = () => {
    if (isLoggedIn) {
      navigate("/");
    } else {
      dispatch(setToggleAuthModal(true));
    }
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Banner */}
        <FeaturesBanner backgroundImage={heroBanner} onSignUpClick={handleSignUpClick} />

        {/* Features Section */}
        <Box
          sx={{
            pt: { xs: 6, md: 16 },
            pb: { xs: 1, md: 10, lg: 16 },
            px: { xs: 2, lg: 6, xl: 14 },
          }}
        >
          {/* Section Title */}
          <Typography
            variant="h4"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              lineHeight: 1.3,
              mb: { xs: 4, md: 6 },
              maxWidth: 400,
            }}
          >
            We've made managing home simpler for you
          </Typography>

          {/* Features Grid */}
          <Grid2 container spacing={{ xs: 3, md: 4 }}>
            {featuresData.map((feature, index) => (
              <Grid2 key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  comingSoon={feature.comingSoon}
                />
              </Grid2>
            ))}
          </Grid2>

          <Box
            sx={{
              mt: { xs: 5, md: 8 },
              p: { xs: 3, md: 4 },
              borderRadius: "16px",
              backgroundColor: "#f6f9f9",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                A question, or a feature you'd like?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tell us. A real person on our team reads every message.
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/contact?topic=feedback"
              variant="contained"
              color="secondary"
              sx={{ textTransform: "none", color: "#fff", borderRadius: "8px", px: 4, flexShrink: 0 }}
            >
              Send us a message
            </Button>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default Features;
